import { showConfirmation } from '../lib/confirmation';
import { formatDate as format, normalizeSearch } from '../lib/format';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import SearchInput from '../components/ui/SearchInput';
import Button from '../components/ui/Button';
import { useState, useEffect, useRef } from 'react';
import Input from '../components/Input';
import Dropdown from '../components/Dropdown';
import { getDropdownValue, postWithAuth, randomString } from '../common';
import { useSelector } from 'react-redux';
import Header from "../components/Header";
import Sidebar from '../components/Sidebar';
import Section from '../components/Section';
import { PlusIcon, BoxesIcon, EditIcon, TrashIcon, EyeIcon } from '../components/NavIcons';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useTableSort from '../components/useTableSort';
import MyCalendar from '../components/Calendar';
import MyDropzone from '../components/MyDropzone';
import ModalComponent from '../components/Modal';
import { equipmentOptions } from '../constants';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import LoadingPopup from '../components/LoadingPopup';
import { resolveStorageFileUrl } from '../storageUpload';
import { isPdfFile } from '../components/MyDropzone';
const previewBoxStyle = {
    border: '1px solid #e6ebf2',
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
}

// New uploads store a storage path in `url`, not a web address, so it has to be
// resolved before it can be shown or opened.
const useStoredFileUrl = (file, orgId) => {
    const [url, setUrl] = useState('')
    useEffect(() => {
        let isCurrent = true
        setUrl('')
        if (!file?.url) return undefined
        resolveStorageFileUrl(file, orgId || '')
            .then(resolved => { if (isCurrent) setUrl(resolved || '') })
            .catch(() => { if (isCurrent) setUrl('') })
        return () => { isCurrent = false }
    }, [file, orgId])
    return url
}

const StoredFilePreview = ({ file, orgId, label, imageClassName }) => {
    const url = useStoredFileUrl(file, orgId)
    if (!file?.url) return <span className='assetEmptyValue'>No preview available</span>
    if (!url) return <span className='assetEmptyValue'>Loading...</span>
    return isPdfFile(file)
        ? <a href={url} target='_blank' rel='noreferrer'>Open PDF</a>
        : <img alt={label} src={url} className={imageClassName} />
}

const formatAssetDisplayName = (asset) => {
    const type = asset?.rateType || asset?.assetType || 'Asset'
    const details = [asset?.machineId, asset?.make, asset?.model].filter(Boolean).join(' ')
    const registration = asset?.regoNo ? ` (${asset.regoNo})` : ''
    return `${type} — ${details}${registration}`.trim()
}

const Assets = () => {

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const activeOrgInfo = useSelector((state) => state.user.activeOrgInfo)

    const [assets, setAssets] = useState([])
    const [searchParams, setSearchParams] = useSearchParams()
    const assetIdFromUrl = searchParams.get('assetId') || ''
    const [viewingAsset, setViewingAsset] = useState('')
    const [assetSearch, setAssetSearch] = useState('')
    const assetListScrollPosition = useRef(null)

    const [showAdd, setShowAdd] = useState(false)
    const [assetType, setAssetType] = useState('')

    const [loading, setLoading] = useState(false)
    const [editing, setEditing] = useState(false)

    const [machineId, setMachineId] = useState('')
    const [make, setMake] = useState('')
    const [model, setModel] = useState('')
    const [rateType, setRateType] = useState('')
    const [assetYear, setAssetYear] = useState('')
    const [regoExpiry, setRegoExpiry] = useState('')
    const [regoNo, setRegoNo] = useState('')
    const [regoPicture, setRegoPicture] = useState('')

    const [insuranceExpiry, setInsuranceExpiry] = useState('')
    const [insurancePicture, setInsurancePicture] = useState('')

    const [addKms, setAddKms] = useState(false)
    const [addKmsIndex, setAddKmsIndex] = useState('')
    const [kmsValue, setKmsValue] = useState('')
    const [kmsDate, setKmsDate] = useState('')
    const [kms, setKms] = useState([])

    const [addService, setAddService] = useState(false)
    const [addServiceIndex, setAddServiceIndex] = useState('')
    const [servicePicture, setServicePicture] = useState('')
    const [serviceDate, setServiceDate] = useState('')
    const [serviceType, setServiceType] = useState('')
    const [serviceNote, setServiceNote] = useState('')
    const [serviceKms, setServiceKms] = useState('')
    const [serviceHistory, setServiceHistory] = useState([])
    const [serviceImagePreview, setServiceImagePreview] = useState(null)

    const [addPicture, setAddPicture] = useState(false)
    const [addPictureIndex, setAddPictureIndex] = useState('')
    const [picture, setPicture] = useState('')
    const [pictureDate, setPictureDate] = useState('')
    const [pictureNote, setPictureNote] = useState('')
    const [pictures, setPictures] = useState([])
    const [equipment, setEquipment] = useState({})


    const [weekdayMorningsRate, setWeekDayMorningsRate] = useState('') // M-F 6am-6pm
    const [weekdayNightsRate, setWeekDayNightsRate] = useState('') // M-F 6pm-6am - not friday night
    const [fridayNightRate, setFridayNightRate] = useState('') // Friday Night Rate

    const [saturdayDayRate, setSaturdayDayRate] = useState('') // Sat Day
    const [saturdayNightRate, setSaturdayNightRate] = useState('') // Sat Night

    const [sundayDayRate, setSundayDayRate] = useState('') // Sun Day
    const [sundayNightRate, setSundayNightRate] = useState('') // Sun Night


    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        if (activeOrg) {
            fetchAssets()
        }
    }, [activeOrg])

    const openAssetPanel = (asset) => {
        assetListScrollPosition.current = window.scrollY
        if (asset?.id) {
            const next = new URLSearchParams(searchParams)
            next.set('assetId', asset.id)
            setSearchParams(next)
        }
        setViewingAsset(asset)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const closeAssetPanel = () => {
        if (assetIdFromUrl) {
            const next = new URLSearchParams(searchParams)
            next.delete('assetId')
            setSearchParams(next)
        }
        setEditing(false)
        resetRowForms()
        setViewingAsset()
        if (assetListScrollPosition.current !== null) {
            const savedScrollPosition = assetListScrollPosition.current
            assetListScrollPosition.current = null
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    window.scrollTo({ top: savedScrollPosition, behavior: 'auto' })
                })
            })
        }
    }

    // Open from the URL (deep link / forward navigation).
    useEffect(() => {
        if (!assetIdFromUrl || assets.length === 0) return
        if (viewingAsset?.id === assetIdFromUrl) return
        const match = assets.find(a => a.id === assetIdFromUrl)
        if (match) setViewingAsset(match)
    }, [assetIdFromUrl, assets, viewingAsset])

    // Close when the param disappears (browser Back).
    useEffect(() => {
        if (!assetIdFromUrl && viewingAsset?.id) {
            setEditing(false)
            setViewingAsset()
        }
    }, [assetIdFromUrl])


    const fetchAssets = async () => {

        try {

            const resp = await postWithAuth('fetchAssets', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setAssets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    useEffect(() => {
        if (viewingAsset) {
            setInformation()
        }
    }, [viewingAsset])

    // The KMs / service / picture sub-forms start empty and closed whenever the
    // asset panel is (re)loaded, closed or a new row is started.
    const resetKmsForm = () => {
        setAddKms(false)
        setAddKmsIndex(-1)
        setKmsValue('')
        setKmsDate('')
    }
    const resetServiceForm = () => {
        setAddService(false)
        setAddServiceIndex(-1)
        setServiceType('')
        setServiceNote('')
        setServiceKms('')
        setServicePicture('')
        setServiceDate('')
    }
    const resetPictureForm = () => {
        setAddPicture(false)
        setAddPictureIndex(-1)
        setPicture('')
        setPictureNote('')
        setPictureDate('')
    }
    const resetRowForms = () => {
        resetKmsForm()
        resetServiceForm()
        resetPictureForm()
    }

    const setInformation = () => {
        resetRowForms()
        setAssetType(viewingAsset.assetType || '')
        setMachineId(viewingAsset.machineId || '')
        setMake(viewingAsset.make || '')
        setModel(viewingAsset.model || '')
        setRateType(viewingAsset.rateType || '')
        setAssetYear(viewingAsset.assetYear || '')

        setRegoExpiry(viewingAsset.regoExpiry || '')
        setRegoNo(viewingAsset.regoNo || '')
        setRegoPicture(viewingAsset.regoPicture || '')

        setInsuranceExpiry(viewingAsset.insuranceExpiry || '')
        setInsurancePicture(viewingAsset.insurancePicture || '')

        setKms(viewingAsset.kms || [])
        setServiceHistory(viewingAsset.serviceHistory || [])
        setPictures(viewingAsset.pictures || [])
        setEquipment(viewingAsset.equipment || {})


        setWeekDayMorningsRate(viewingAsset.weekdayMorningsRate || '')
        setWeekDayNightsRate(viewingAsset.weekdayNightsRate || '')
        setFridayNightRate(viewingAsset.fridayNightRate || '')
        setSaturdayDayRate(viewingAsset.saturdayDayRate || '')
        setSaturdayNightRate(viewingAsset.saturdayNightRate || '')
        setSundayDayRate(viewingAsset.sundayDayRate || '')
        setSundayNightRate(viewingAsset.sundayNightRate || '')


    }

    const assetDetailsComplete = Boolean(machineId && rateType && regoExpiry && regoNo && make && model && assetYear)

    const saveAssetInfo = async (createFromModal = false, { keepOpen = false, overrides = {} } = {}) => {

        try {
            setLoading(true)

            const payload = {
                id: createFromModal ? '' : viewingAsset?.id,

                machineId,
                assetType,
                rateType: rateType || '',
                make,
                model,
                assetYear,
                regoExpiry,
                regoNo,
                regoPicture,
                insuranceExpiry,
                insurancePicture,


                weekdayMorningsRate,
                weekdayNightsRate,
                fridayNightRate,
                saturdayDayRate,
                saturdayNightRate,
                sundayDayRate,
                sundayNightRate,

                kms,
                serviceHistory,
                pictures,
                equipment,
                orgId: activeOrg.id,
                ...overrides,
            }

            await postWithAuth('saveAssetInfo', payload)

            if (keepOpen) {
                // A KMs / service / picture row was saved: stay on the asset.
                const { orgId, ...savedAsset } = payload
                setEditing(false)
                setViewingAsset(current => ({ ...current, ...savedAsset }))
                showConfirmation('Asset information saved')
                setLoading(false)
                fetchAssets()
                return true
            }

            if (createFromModal) {
                setShowAdd(false)
                setViewingAsset()
            }

            setEditing(false)
            showConfirmation('Asset information saved')
            setLoading(false)
            await fetchAssets()
            if (!createFromModal) closeAssetPanel()
            return true

        } catch (e) {
            toast.error(e.message)
            setLoading(false)
            return false
        }
    }

    // Saves one changed row list (KMs, service history or pictures) with the
    // asset and keeps the panel open; the row is rolled back if it cannot be saved.
    const saveAssetRows = async (field, nextRows, previousRows, setRows) => {
        if (!assetDetailsComplete) {
            toast.error('Complete the required asset details before saving this row.')
            return false
        }
        setRows(nextRows)
        const saved = await saveAssetInfo(false, { keepOpen: true, overrides: { [field]: nextRows } })
        if (!saved) setRows(previousRows)
        return saved
    }

    const handleSaveAssetInfo = () => saveAssetInfo(false)
    const handleCreateAsset = () => saveAssetInfo(true)

    const openAddAssetModal = () => {
        setAssetType('')
        setMachineId('')
        setMake('')
        setModel('')
        setRateType('')
        setAssetYear('')
        setRegoExpiry('')
        setRegoNo('')
        setRegoPicture('')
        setInsuranceExpiry('')
        setInsurancePicture('')
        // A new asset starts empty; these used to carry over from the last asset opened.
        setKms([])
        setServiceHistory([])
        setPictures([])
        setEquipment({})
        setWeekDayMorningsRate('')
        setWeekDayNightsRate('')
        setFridayNightRate('')
        setSaturdayDayRate('')
        setSaturdayNightRate('')
        setSundayDayRate('')
        setSundayNightRate('')
        setShowAdd(true)
    }


    const handleAddKms = async () => {
        const row = { kms: kmsValue, date: kmsDate.toString() }
        const nextKms = addKmsIndex >= 0
            ? kms.map((item, idx) => idx === addKmsIndex ? row : item)
            : [...kms, row]
        if (await saveAssetRows('kms', nextKms, kms, setKms)) resetKmsForm()
    }

    const handleAddService = async () => {
        const row = { serviceType, note: serviceNote, serviceKms, picture: servicePicture, date: serviceDate.toString() }
        const nextServiceHistory = addServiceIndex >= 0
            ? serviceHistory.map((item, idx) => idx === addServiceIndex ? row : item)
            : [...serviceHistory, row]
        if (await saveAssetRows('serviceHistory', nextServiceHistory, serviceHistory, setServiceHistory)) resetServiceForm()
    }

    const handleAddPicture = async () => {
        const row = { note: pictureNote, picture: picture, date: pictureDate.toString() }
        const nextPictures = addPictureIndex >= 0
            ? pictures.map((item, idx) => idx === addPictureIndex ? row : item)
            : [...pictures, row]
        if (await saveAssetRows('pictures', nextPictures, pictures, setPictures)) resetPictureForm()
    }

    const renderPreview = (file, label) => (
        <StoredFilePreview file={file} orgId={activeOrg?.id} label={label} imageClassName='assetPreviewImage' />
    )

    const assetsSort = useTableSort(assets, { key: 'name', direction: 'ascending' });
    const assetSearchQuery = normalizeSearch(assetSearch.trim())
    const showExpiredAssets = searchParams.get('filter') === 'expired'
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    const visibleAssets = assetsSort.sortedData
        .filter(asset => !showExpiredAssets || [asset.regoExpiry, asset.insuranceExpiry].some(value => {
            if (!value) return false
            const expiryDate = new Date(value)
            return !Number.isNaN(expiryDate.getTime()) && expiryDate < todayStart
        }))
        .filter(asset => !assetSearchQuery || normalizeSearch(`${asset.machineId || ''} ${asset.regoNo || ''} ${asset.make || ''} ${asset.model || ''}`).includes(assetSearchQuery))
    const addAssetReady = machineId && make && model && rateType && assetYear
        && regoExpiry && regoNo && regoPicture && insuranceExpiry && insurancePicture


    return (<div>
        <Header />
        <LoadingPopup visible={loading} message="Loading..." />
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent pageContentPadded assetPageContent'>
                <PageHeader title={"Assets & Equipment"} subtitle={<>Manage vehicles, plant and equipment</>} />

                <ModalComponent
                    showModal={showAdd}
                    setShowModal={setShowAdd}
                    customMaxWidth='900px'
                    customContentStyle={{ padding: 0, width: '92%', maxHeight: 'calc(100vh - 24px)' }}
                >
                    <div className='assetAddModal'>
                        <div className='assetAddModalHeader'>
                            <h2 className='modalTitle'>Add Asset</h2>
                            <div className='assetAddModalHeaderActions'>
                                <Button variant="secondary" size="md" className='secondaryButton' onClick={() => setShowAdd(false)}>Cancel</Button>
                                <Button data-action-tone="save" variant="soft" size="md"  className='assetModalSaveButton saveWithIcon' disabled={!addAssetReady || loading || uploading} onClick={handleCreateAsset}>
                                    {loading ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </div>

                        <div className='assetAddModalBody'>
                            <div className='assetAddModalSections'>
                                <Section title={'Asset Details'} className='width-100 assetAddModalSection' variant='card'>
                                    <div className='assetAddModalGrid assetAddModalGridThree'>
                                        <Input disabled={false} value={machineId} setValue={setMachineId} placeholder={'e.g. MC01'} label={'Asset ID'} required />
                                        <Input disabled={false} value={make} setValue={setMake} placeholder={'Make'} label={'Make'} required />
                                        <Input disabled={false} value={model} setValue={setModel} placeholder={'Model'} label={'Model'} required />
                                        <Dropdown
                                            label={'Asset Type'}
                                            required
                                            placeholder={'Select asset type'}
                                            options={equipmentOptions.map(type => ({ label: type, value: type }))}
                                            value={getDropdownValue(rateType, equipmentOptions.map(type => ({ label: type, value: type })))}
                                            setValue={value => {
                                                const selectedType = value?.value || ''
                                                setRateType(selectedType)
                                                setAssetType(selectedType === 'Work ute' ? 'Vehicle' : selectedType ? 'Plant & Machinery' : '')
                                            }}
                                        />
                                        <MyCalendar
                                            disabled={false}
                                            showDateOnly
                                            showYearDropdown
                                            showYearPicker
                                            startDate={assetYear}
                                            setStartDate={setAssetYear}
                                            label={'Year'}
                                            placeholder={'Select year'}
                                            dateFormat='yyyy'
                                            required
                                        />
                                    </div>
                                </Section>

                                <Section title={'Registration Details'} className='width-100 assetAddModalSection' variant='card'>
                                    <div className='assetAddModalGrid assetAddModalGridThree'>
                                        <MyCalendar disabled={false} minDate={new Date()} noMaxDate showDateOnly showYearDropdown startDate={regoExpiry} setStartDate={setRegoExpiry} label={'Expiry Date'} placeholder={'Select date'} required />
                                        <Input disabled={false} value={regoNo} setValue={setRegoNo} placeholder={'Registration Number'} label={'Registration number'} required />
                                        <MyDropzone
                                            setUploading={setUploading}
                                            key={`add-rego-${regoPicture?.url || ''}`}
                                            disabled={false}
                                            storageLocation={`AssetDocs/${randomString()}`}
                                            label={'Registration Documentation'}
                                            customMessage={<span className='assetAddUploadMessage'><UploadFileOutlinedIcon /><span>Click here/Drop files here to upload</span></span>}
                                            editing={true}
                                            errors={[]}
                                            allowPdf
                                            required
                                            name={'addAssetRegistrationDocument'}
                                            image={regoPicture}
                                            updateImage={setRegoPicture}
                                        />
                                    </div>
                                </Section>

                                <Section title={'Insurance Details'} className='width-100 assetAddModalSection' variant='card'>
                                    <div className='assetAddModalGrid'>
                                        <MyCalendar disabled={false} minDate={new Date()} noMaxDate showDateOnly showYearDropdown startDate={insuranceExpiry} setStartDate={setInsuranceExpiry} label={'Expiry Date'} placeholder={'Select date'} required />
                                        <MyDropzone
                                            setUploading={setUploading}
                                            key={`add-insurance-${insurancePicture?.url || ''}`}
                                            disabled={false}
                                            storageLocation={`AssetDocs/${randomString()}`}
                                            label={'Insurance Documentation'}
                                            customMessage={<span className='assetAddUploadMessage'><UploadFileOutlinedIcon /><span>Click here/Drop files here to upload</span></span>}
                                            editing={true}
                                            errors={[]}
                                            allowPdf
                                            required
                                            name={'addAssetInsuranceDocument'}
                                            image={insurancePicture}
                                            updateImage={setInsurancePicture}
                                        />
                                    </div>
                                </Section>
                            </div>
                        </div>

                        <div className='assetAddModalFooter assetAddModalFooterLegacy'>
                            <Button variant="secondary" size="md" className='secondaryButton' onClick={() => setShowAdd(false)}>Cancel</Button>
                            <Button data-action-tone="save" variant="soft" size="md"  className='assetModalSaveButton saveWithIcon' disabled={!addAssetReady || loading || uploading} onClick={handleCreateAsset}>
                                {loading ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={Boolean(serviceImagePreview)}
                    setShowModal={(show) => { if (!show) setServiceImagePreview(null) }}
                    customMaxWidth='720px'
                    customContentStyle={{ padding: 0, width: '90%' }}
                >
                    <div className='assetServiceImageModal'>
                        <div className='assetServiceImageModalHeader'>
                            <h3>Service Image</h3>
                            <Button variant="secondary" size="md" className='secondaryButton' onClick={() => setServiceImagePreview(null)}>Cancel</Button>
                        </div>
                        <div className='assetServiceImageModalBody'>
                            {serviceImagePreview?.url && <StoredFilePreview file={serviceImagePreview} orgId={activeOrg?.id} label='Service' />}
                        </div>
                    </div>
                </ModalComponent>

                {viewingAsset ? <>
                    <div className='assetExistingProfileModal'>
                    <div className='assetProfileShell'>
                        <div className='assetProfileToolbar'>
                            <div className='assetProfileModalTitleBlock'>
                                <h3 className='pageTitle'>Asset Profile</h3>
                                <p className='pageSubtitle'>{machineId ? `${machineId}${regoNo ? ` - ${regoNo}` : ''}` : 'Asset details'}</p>
                            </div>
                            <div className='assetProfileHeaderActions'>
                            {editing ?
                                <>
                                    <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton assetTopActionButton' onClick={() => { setEditing(false); setInformation() }}>Cancel</Button>
                                    <Button data-action-tone="save" variant="soft" size="md"  className='assetTopActionButton saveWithIcon' disabled={!machineId || !rateType || !regoExpiry || !regoNo || !make || !model || !assetYear || loading || uploading} onClick={handleSaveAssetInfo}>Save</Button>
                                </>
                                : <>
                                    <Button variant="secondary" size="md" className='secondaryButton assetTopActionButton' onClick={closeAssetPanel}>Cancel</Button>
                                    <Button data-action-tone="edit" variant="secondary" size="md"  className='assetTopActionButton actionWithIcon' onClick={() => setEditing(true)}><EditIcon />Edit</Button>
                                </>}
                            </div>
                        </div>
                        <div className='recordHeaderBar assetProfileHeader'>
                            <div className='recordHeaderIdentity'>
                                <div className='recordHeaderAvatar'><BoxesIcon style={{ width: 18, height: 18 }} /></div>
                                <div>
                                    <div className='recordHeaderTitle'>{machineId ? `${machineId}${regoNo ? ` - ${regoNo}` : ''}` : 'New Asset'}</div>
                                    <div className='recordHeaderMeta'>{[make, model].filter(Boolean).join(' ') || 'No make/model on file'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {<>
                        <div className='assetProfileSectionStack'>
                            <Section title={'Asset Details'} className='width-100 assetProfileCard' variant='card'>
                                <div className='assetProfileFieldGrid assetProfileFieldGridTwo'>
                                    <Input disabled={!editing} value={machineId} setValue={setMachineId} placeholder={'Asset Id'} label={'Asset Id'} required />
                                    <Dropdown label={'Equipment Type'} disabled={!editing} required options={equipmentOptions.map(r => ({ label: r, value: r }))} value={getDropdownValue(rateType, equipmentOptions.map(r => ({ label: r, value: r })))} setValue={v => {
                                        const selectedType = v?.value || ''
                                        setRateType(selectedType)
                                        setAssetType(selectedType === 'Work ute' ? 'Vehicle' : selectedType ? 'Plant & Machinery' : '')
                                    }} />
                                    <Input disabled={!editing} value={make} setValue={setMake} placeholder={'Make'} label={'Make'} required />
                                    <Input disabled={!editing} value={model} setValue={setModel} placeholder={'Model'} label={'Model'} required />
                                    <MyCalendar disabled={!editing} showDateOnly showYearDropdown startDate={assetYear} setStartDate={setAssetYear} label={'Year'} />
                                </div>
                            </Section>
                            <Section title={'Registration Details'} className='width-100 assetProfileCard' variant='card'>
                                <div className='assetProfileFieldGrid assetRegistrationDetailsGrid'>
                                    <div className='assetRegistrationNumberField'>
                                        <Input disabled={!editing} value={regoNo} setValue={setRegoNo} placeholder={'Registration Number'} label={'Registration Number'} required />
                                    </div>
                                    <div className='assetRegistrationUploadField'>
                                        <MyDropzone setUploading={setUploading} key={regoPicture} disabled={!editing} storageLocation={`AssetDocs/${randomString()}`} label={'Registration Document'} customMessage={'Click here/Drop files here to upload'} editing={true} errors={[]} allowPdf name={'document'} image={regoPicture} updateImage={(doc) => setRegoPicture(doc)} />
                                    </div>
                                    <div className='assetRegistrationDocumentField'>
                                        <div className='assetAttachedDocumentsHeading'>Attached Documents</div>
                                    </div>
                                    <div className='assetRegistrationExpiryField'>
                                        <MyCalendar disabled={!editing} minDate={new Date()} noMaxDate showDateOnly showYearDropdown startDate={regoExpiry} setStartDate={setRegoExpiry} label={'Registration Expiry'} required />
                                    </div>
                                </div>
                            </Section>
                            <Section title={'Insurance Details'} className='width-100 assetProfileCard' variant='card'>
                                <div className='assetProfileFieldGrid assetInsuranceDetailsGrid'>
                                    <div className='assetInsuranceExpiryField'>
                                        <MyCalendar required disabled={!editing} minDate={new Date()} noMaxDate showDateOnly showYearDropdown startDate={insuranceExpiry} setStartDate={setInsuranceExpiry} label={'Insurance Expiry'} />
                                    </div>
                                    <div className='assetInsuranceUploadField'>
                                        <MyDropzone setUploading={setUploading} key={insurancePicture} disabled={!editing} storageLocation={`AssetDocs/${randomString()}`} label={'Insurance Document'} customMessage={'Click here/Drop files here to upload'} editing={true} errors={[]} allowPdf name={'document'} image={insurancePicture} updateImage={(doc) => setInsurancePicture(doc)} />
                                    </div>
                                    <div className='assetInsuranceDocumentField'>
                                        <div className='assetAttachedDocumentsHeading'>Attached Documents</div>
                                    </div>
                                </div>
                            </Section>
                            {viewingAsset.id && <>

                                <Section title={'Kilometers'} className='width-100 assetProfileCard' variant='card'>
                                    {addKms ?
                                        <>
                                            <div className='assetProfileFieldGrid assetProfileFieldGridTwo'>
                                                <Input disabled={false} value={kmsValue} setValue={setKmsValue} placeholder={'Kms'} label={'Kms'} required />
                                                <MyCalendar required disabled={false} noMaxDate showDateOnly startDate={kmsDate} setStartDate={setKmsDate} label={'Date'} />
                                            </div>
                                            <div className='modalFooter'>
                                                <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={resetKmsForm}>Cancel</Button>
                                                <Button data-action-tone="save" variant="soft" size="md"  className='assetInlineSaveButton saveWithIcon' disabled={!kmsValue || !kmsDate} onClick={handleAddKms}>Save</Button>
                                            </div>

                                        </>
                                        :
                                        <>
                                            <Button variant="create" size="md" className='assetInlineActionButton' onClick={() => { resetKmsForm(); setAddKms(true) }}>+ KMs</Button>
                                            <div className='assetTableWrap'>
                                            <Table emptyItems="kilometre entries" className='assetsDirectoryTable assetKilometersTable'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableHeaderCell>Date</TableHeaderCell>
                                                        <TableHeaderCell>Kilometers</TableHeaderCell>
                                                        <TableHeaderCell>Action</TableHeaderCell>
                                                    </TableRow>
                                                </TableHead>
                                                <tbody>
                                                    {kms.map((row, index) => <TableRow key={`kms-${index}`}>
                                                        <TableCell>{format(new Date(row.date), 'EEE, d MMM yyyy')}</TableCell>
                                                        <TableCell>{row.kms}</TableCell>
                                                        <TableCell>
                                                            <div className='assetServiceActionIcons'>
                                                                <Button data-action-tone="edit" variant="secondary" size="icon"  className='recordActionIcon assetServiceEditIcon' aria-label='Edit kilometers' title='Edit' onClick={() => { setAddKms(true); setAddKmsIndex(index); setKmsValue(row.kms); setKmsDate(row.date) }}><EditIcon /></Button>
                                                                <Button data-action-tone="delete" variant="danger" size="icon"  className='recordActionIcon assetServiceDeleteIcon' aria-label='Delete kilometers' title='Delete' onClick={() => { if (window.confirm('Delete')) saveAssetRows('kms', kms.filter((r, idx) => idx !== index), kms, setKms) }}><TrashIcon /></Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>)}
                                                </tbody>
                                            </Table>
                                            </div>
                                        </>}
                                </Section>

                                <Section title={'Service History'} className='width-100 assetProfileCard' variant='card'>
                                    {addService ?
                                        <>
                                            <div className='assetProfileFieldGrid assetProfileFieldGridTwo'>
                                                <Input disabled={false} value={serviceType} setValue={setServiceType} placeholder={'Type'} label={'Service Type'} required />
                                                <MyCalendar required disabled={false} noMaxDate showDateOnly startDate={serviceDate} setStartDate={setServiceDate} label={'Service Date'} />
                                                <Input disabled={false} value={serviceKms} setValue={setServiceKms} placeholder={'Kms'} label={'Service Kms'} required />
                                                <Input disabled={false} value={serviceNote} setValue={setServiceNote} placeholder={'Note'} label={'Comments'} />
                                                <div className='assetProfileFieldSpanFull'>
                                                    <MyDropzone setUploading={setUploading} key={servicePicture} disabled={false} storageLocation={`AssetDocs/${randomString()}`} label={'Service Picture'} customMessage={'Click here/Drop files here to upload'} editing={true} errors={[]} allowPdf name={'document'} image={servicePicture} updateImage={(doc) => setServicePicture(doc)} />
                                                </div>
                                            </div>

                                            <div className='modalFooter'>
                                                <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={resetServiceForm}>Cancel</Button>
                                                <Button data-action-tone="save" variant="soft" size="md"  className='assetInlineSaveButton saveWithIcon' disabled={!serviceType || !serviceKms || !serviceDate || uploading} onClick={handleAddService}>Save</Button>
                                            </div>

                                        </>
                                        :
                                        <>
                                            <Button variant="create" size="md" className='assetInlineActionButton' onClick={() => { resetServiceForm(); setAddService(true) }}>+ Service</Button>
                                            <div className='assetTableWrap'>
                                            <Table emptyItems="service entries" className='assetsDirectoryTable assetDataTableFixed assetServiceHistoryTable'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableHeaderCell className='assetColDate'>Service Date</TableHeaderCell>
                                                        <TableHeaderCell className='assetColType'>Type</TableHeaderCell>
                                                        <TableHeaderCell className='assetColKms'>Service Kms</TableHeaderCell>
                                                        <TableHeaderCell className='assetColComment'>Comments</TableHeaderCell>
                                                        <TableHeaderCell className='assetColImage'>Image</TableHeaderCell>
                                                        <TableHeaderCell className='assetColAction'>Action</TableHeaderCell>
                                                    </TableRow>
                                                </TableHead>
                                                <tbody>
                                                    {serviceHistory.map((row, index) => (
                                                        <TableRow key={`service-${index}`}>
                                                            <TableCell className='assetColDate'>{format(new Date(row.date), 'EEE, d MMM yyyy')}</TableCell>
                                                            <TableCell className='assetColType assetCellBreak'>{row.serviceType || '-'}</TableCell>
                                                            <TableCell className='assetColKms'>{row.serviceKms}</TableCell>
                                                            <TableCell className='assetColComment assetCellBreak'>{row.note || '-'}</TableCell>
                                                            <TableCell className='assetColImage'>
                                                                {row.picture?.url
                                                                    ? <Button variant="secondary" size="icon" className='recordActionIcon assetServiceViewIcon' aria-label='View service image' title='View' onClick={() => setServiceImagePreview(row.picture)}><EyeIcon /></Button>
                                                                    : <span className='assetEmptyValue'>-</span>}
                                                            </TableCell>
                                                            <TableCell className='assetColAction'>
                                                                <div className='assetActionStack assetServiceActionIcons'>
                                                                    <Button data-action-tone="edit" variant="secondary" size="icon"  className='recordActionIcon assetServiceEditIcon' aria-label='Edit service' title='Edit' onClick={() => { setAddService(true); setAddServiceIndex(index); setServiceType(row.serviceType); setServiceNote(row.note); setServiceKms(row.serviceKms); setServicePicture(row.picture); setServiceDate(row.date) }}><EditIcon /></Button>
                                                                    <Button data-action-tone="delete" variant="danger" size="icon"  className='recordActionIcon assetServiceDeleteIcon' aria-label='Delete service' title='Delete' onClick={() => { if (window.confirm('Delete')) saveAssetRows('serviceHistory', serviceHistory.filter((r, idx) => idx !== index), serviceHistory, setServiceHistory) }}><TrashIcon /></Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            </div>
                                        </>}
                                </Section>

                                <Section title={'Pictures'} className='width-100 assetProfileCard' variant='card'>
                                    {addPicture ?
                                        <>
                                            <div className='assetProfileFieldGrid assetProfileFieldGridTwo'>
                                                <Input disabled={false} value={pictureNote} setValue={setPictureNote} placeholder={'Note'} label={'Type/Note'} required />
                                                <MyCalendar required disabled={false} noMaxDate showDateOnly startDate={pictureDate} setStartDate={setPictureDate} label={'Date'} />
                                                <div className='assetProfileFieldSpanFull'>
                                                    <MyDropzone setUploading={setUploading} key={picture} disabled={false} storageLocation={`AssetDocs/${randomString()}`} label={'Picture'} customMessage={'Click here/Drop files here to upload'} editing={true} errors={[]} allowPdf name={'document'} image={picture} updateImage={(doc) => setPicture(doc)} />
                                                </div>
                                            </div>

                                            <div className='modalFooter'>
                                                <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={resetPictureForm}>Cancel</Button>
                                                <Button data-action-tone="save" variant="soft" size="md"  className='assetInlineSaveButton saveWithIcon' disabled={!pictureNote || !picture || !pictureDate || uploading} onClick={handleAddPicture}>Save</Button>
                                            </div>

                                        </>
                                        :
                                        <>
                                            <Button variant="create" size="md" className='assetInlineActionButton' onClick={() => { resetPictureForm(); setAddPicture(true) }}>+ Picture</Button>
                                            <div className='assetTableWrap'>
                                            <Table emptyItems="documents" className='assetsDirectoryTable assetDataTableFixed'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableHeaderCell className='assetColWide'>Type/Note</TableHeaderCell>
                                                        <TableHeaderCell className='assetColDate'>Date</TableHeaderCell>
                                                        <TableHeaderCell className='assetColImageWide'>Image</TableHeaderCell>
                                                        <TableHeaderCell className='assetColActionWide'>Action</TableHeaderCell>
                                                    </TableRow>
                                                </TableHead>
                                                <tbody>
                                                    {pictures.map((row, index) => (
                                                        <TableRow key={`service-${index}`}>
                                                            <TableCell className='assetCellBreak'>{row.note || `Picture ${index + 1}`}</TableCell>
                                                            <TableCell>{format(new Date(row.date), 'EEE, d MMM yyyy')}</TableCell>
                                                            <TableCell>
                                                                <div style={previewBoxStyle} className='assetPreviewBox assetPreviewBoxCompact'>
                                                                    {renderPreview(row.picture, row.note || `Picture ${index + 1}`)}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className='assetActionStack'>
                                                                    <Button data-action-tone="edit" variant="secondary" size="sm"  className='textButton actionWithIcon' onClick={() => { setAddPicture(true); setAddPictureIndex(index); setPictureNote(row.note); setPicture(row.picture); setPictureDate(row.date) }}><EditIcon />Edit</Button>
                                                                    <Button data-action-tone="delete" variant="danger" size="sm"  className='textButton' onClick={() => { if (window.confirm('Delete')) saveAssetRows('pictures', pictures.filter((r, idx) => idx !== index), pictures, setPictures) }}>Delete</Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            </div>
                                        </>}
                                </Section>
                                {/* <Section title={'Rates'} className='width-50'>


                                    <RatesTableSingle

                                        weekdayMorningsRate={weekdayMorningsRate}
                                        setWeekDayMorningsRate={setWeekDayMorningsRate}
                                        weekdayNightsRate={weekdayNightsRate}
                                        setWeekDayNightsRate={setWeekDayNightsRate}
                                        fridayNightRate={fridayNightRate}
                                        setFridayNightRate={setFridayNightRate}
                                        saturdayDayRate={saturdayDayRate}
                                        setSaturdayDayRate={setSaturdayDayRate}
                                        saturdayNightRate={saturdayNightRate}
                                        setSaturdayNightRate={setSaturdayNightRate}
                                        sundayDayRate={sundayDayRate}
                                        setSundayDayRate={setSundayDayRate}
                                        sundayNightRate={sundayNightRate}
                                        setSundayNightRate={setSundayNightRate}
                                        editing={editing}
                                    />

                                </Section> */}
                                {assetType === 'Vehicle' && <Section title={'Equipment'} className='width-100 assetProfileCard' variant='card'>
                                    <div className='assetProfileFieldGrid assetProfileFieldGridTwo'>
                                        <Input disabled={!editing} value={equipment['Signs']} setValue={v => setEquipment({ ...equipment, Signs: v })} placeholder={'Signs'} label={'Signs'} />
                                        <Input disabled={!editing} value={equipment['Cones']} setValue={v => setEquipment({ ...equipment, Cones: v })} placeholder={'Cones'} label={'Cones'} />
                                        <Input disabled={!editing} value={equipment['Bats']} setValue={v => setEquipment({ ...equipment, Bats: v })} placeholder={'Bats'} label={'Bats'} />
                                        <Input disabled={!editing} value={equipment['Bollards']} setValue={v => setEquipment({ ...equipment, Bollards: v })} placeholder={'Bollards'} label={'Bollards'} />
                                        <Input disabled={!editing} value={equipment['Barriers']} setValue={v => setEquipment({ ...equipment, Barriers: v })} placeholder={'Barriers'} label={'Barriers'} />
                                    </div>
                                </Section>}

                            </>}
                        </div>
                    </>}
                    </div>
                </> : <>
                    <div className='assetsDirectoryLayout'>
                        <Section title={showExpiredAssets ? 'Expired Registration & Insurance' : 'Assets'} className='width-70 assetsDirectoryCard' variant='card'>
                            <div className='listToolbar assetsDirectoryToolbar'>
                                <div className='listToolbarSearch'>
                                    <div className="dashboardSearchField uiSearchContainer">

                                        <SearchInput type='text' value={assetSearch} placeholder='Search' onChange={(e) => setAssetSearch(e.target.value)} />
                                    </div>
                                </div>
                                <Button variant="create" size="md" className='allocationNewButton' onClick={openAddAssetModal}>
                                    <PlusIcon />
                                    Asset
                                </Button>
                            </div>

                            <div className='assetTableWrap'>
                            <Table className='assetsDirectoryTable'>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell {...assetsSort.getSortableHeaderProps('machineId')}>
                                            Asset ID
                                        </TableHeaderCell>
                                        <TableHeaderCell {...assetsSort.getSortableHeaderProps('regoExpiry')}>
                                            Registration Expiry
                                        </TableHeaderCell>
                                        <TableHeaderCell {...assetsSort.getSortableHeaderProps('insuranceExpiry')}>
                                            Insurance Expiry
                                        </TableHeaderCell>
                                        <TableHeaderCell {...assetsSort.getSortableHeaderProps('rateType')}>
                                            Asset Type
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {visibleAssets.length === 0 ? (
                                        <TableEmpty  colSpan={4}>No assets yet.</TableEmpty>
                                    ) : visibleAssets.map(asset => <TableRow key={asset.id}>
                                        <TableCell onClick={() => openAssetPanel(asset)} className="hover">
                                            <span className='allocationDateLink'>{formatAssetDisplayName(asset)}</span>
                                        </TableCell>
                                        <TableCell>{asset.regoExpiry ? format(new Date(asset.regoExpiry), 'EEE, d MMM yyyy') : '—'}</TableCell>
                                        <TableCell>{asset.insuranceExpiry ? format(new Date(asset.insuranceExpiry), 'EEE, d MMM yyyy') : '—'}</TableCell>
                                        <TableCell>{asset.rateType || '—'}</TableCell>
                                    </TableRow>)}
                                </tbody>
                            </Table>
                            </div>
                        </Section>
                        <Section title={'Asset Location'} className='width-30 assetsLocationCard' variant='card'>
                            <div className='assetsLocationPlaceholder'></div>
                        </Section>
                    </div>
                </>}
            </PageContent>
        </div>
    </div >)
}

export default Assets
